import { supabase } from "@/integrations/supabase/client";

/**
 * Level label i18n keys — use with t(`level.groups.${key}`) from common namespace.
 * Pure function returns style classes + the i18n key (not the translated string).
 */
export function getLevelInfo(level: number): { labelKey: string; label: string; color: string; bgClass: string; textClass: string } {
  if (level <= 2) {
    return {
      labelKey: "beginner",
      label: "Nybörjare", // fallback if no t() available
      color: "hsl(0, 0%, 55%)",
      bgClass: "bg-muted ring-muted-foreground/30",
      textClass: "text-muted-foreground",
    };
  }
  if (level <= 4) {
    return {
      labelKey: "recreational",
      label: "Motionär",
      color: "hsl(142, 71%, 45%)",
      bgClass: "bg-green-500/15 ring-green-500/30",
      textClass: "text-green-600 dark:text-green-400",
    };
  }
  if (level <= 6) {
    return {
      labelKey: "intermediate",
      label: "Medel",
      color: "hsl(217, 91%, 60%)",
      bgClass: "bg-blue-500/15 ring-blue-500/30",
      textClass: "text-blue-600 dark:text-blue-400",
    };
  }
  if (level <= 8) {
    return {
      labelKey: "advanced",
      label: "Avancerad",
      color: "hsl(263, 70%, 50%)",
      bgClass: "bg-purple-500/15 ring-purple-500/30",
      textClass: "text-purple-600 dark:text-purple-400",
    };
  }
  return {
    labelKey: "expert",
    label: "Expert",
    color: "hsl(45, 93%, 47%)",
    bgClass: "bg-yellow-500/15 ring-yellow-500/30",
    textClass: "text-yellow-600 dark:text-yellow-400",
  };
}

// i18n key arrays — consumers should use t(`level.descriptions.${level}`) and t(`level.groupDescriptions.${key}`)
export const LEVEL_DESCRIPTION_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
export const LEVEL_GROUP_KEYS = ["1-2", "3-4", "5-6", "7-8", "9-10"] as const;

/**
 * Calculate effective level for a user based on peer assessments.
 * If ≥5 assessments and median deviates >1 from current level, return median.
 * Otherwise return current level.
 */
export async function calculateEffectiveLevel(userId: string): Promise<number> {
  const { data: profile } = await supabase
    .from("profiles")
    .select("level")
    .eq("id", userId)
    .single();

  const currentLevel = profile?.level ?? 5;

  const { data: assessments } = await supabase
    .from("level_assessments")
    .select("suggested_level")
    .eq("assessed_user_id", userId)
    .order("created_at", { ascending: false })
    .limit(20);

  const values = (assessments ?? []).map((a) => a.suggested_level);

  if (values.length < 5) {
    return currentLevel;
  }

  // Calculate median
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 === 0
    ? Math.round((sorted[mid - 1] + sorted[mid]) / 2)
    : sorted[mid];

  // Only adjust if deviation > 1
  if (Math.abs(median - currentLevel) > 1) {
    return median;
  }

  return currentLevel;
}
